import _ from 'lodash'
import { createSelector } from 'reselect'
import { creditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { userEmailOption } from 'src/components/UserDropdown/UserDropdown'
import { RootState } from 'src/redux/createRootReducer'
import { CreditCardsFullPaginatedDataState } from 'src/redux/creditCardsFullPaginatedData.reducer'
import { RecentUsersFullPaginatedDataState } from 'src/redux/recentUsersFullPaginatedData.reducer'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { ErrorDetailMap } from 'src/utils/error.utils'

export const selectPostingCreditCardFromState = (state: RootState): boolean =>
  state.app.requestBudget.postingCreditCard

export const selectTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.app.requestBudget.topLevelError

export const selectErrorDetailMapFromState = (
  state: RootState,
): ErrorDetailMap<string> | undefined => state.app.requestBudget.errorDetailMap

export const selectRecentUsersFullPaginatedData = (
  state: RootState,
): RecentUsersFullPaginatedDataState =>
  state.app.requestBudget.recentUsersFullPaginatedData

const selectUniqueRecentUserIds = createSelector(
  [selectRecentUsersFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserIds, selectUserListModelsFromState],
  (ids, users) => ids.map((id) => userEmailOption(users[id])),
)

export const selectFilteredRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserEmailOptions, selectUserListModelFromState],
  (options, user) => {
    const ownUser = options.find(
      (option) => _.get(option.details, 'user.id', undefined) === user?.id,
    )
    const filteredOptions = options.filter(
      (option) =>
        _.get(option.details, 'user.id', undefined) !== user?.id &&
        (user?.hasOrgAdminPermissions ||
          user?.organizationId === option.details?.user?.organizationId),
    )
    if (ownUser) {
      filteredOptions.unshift(ownUser)
    }
    return filteredOptions.slice(0, 5)
  },
)

export const selectUsersFullPaginatedData = (
  state: RootState,
): UsersFullPaginatedDataState => state.app.requestBudget.usersFullPaginatedData
const selectUniqueUserIds = createSelector(
  [selectUsersFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueUserEmailOptions = createSelector(
  [selectUniqueUserIds, selectUserListModelsFromState],
  (ids, users) => ids.map((id) => userEmailOption(users[id])),
)
export const selectFilteredUserEmailOptions = createSelector(
  [selectUniqueUserEmailOptions, selectUserListModelFromState],
  (options, user) =>
    options.filter(
      (option) =>
        user?.hasOrgAdminPermissions ||
        user?.organizationId === option.details?.user?.organizationId,
    ),
)

export const selectCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.requestBudget.creditCardsFullPaginatedData
const selectUniqueCreditCardIds = createSelector(
  [selectCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueCreditCardOptions = createSelector(
  [selectUniqueCreditCardIds, selectCreditCardListModelsFromState],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredCreditCardOptions = createSelector(
  [selectUniqueCreditCardOptions],
  (options) => options.filter(() => true),
)
